
import HintTooltip from '@/components/helper/HintTooltip.vue'
import JigTextField from '@/components/input/JigTextField.vue'
import JigTooltip from '@/components/input/JigTooltip.vue'
import LabelWithHint from '@/components/input/LabelWithHint.vue'
import MoreJigActions from '@/components/input/MoreJigActions.vue'
import UsersPreviewList from '@/components/input/UsersPreviewList.vue'
import JigWarningModals from '@/components/jig/JigWarningModals.vue'
import TeamRights from '@/components/jig/TeamRights.vue'
import UploadThumbnail from '@/components/jig/UploadThumbnail.vue'
import ExportJigModal from '@/components/modals/ExportJigModal.vue'
import { extractEnvFromApiDomain } from '@/services/generate-jig/generate-jig.utils'
import JigActionModals from '@/components/modals/JigActionModals.vue'
import { ClosePromptFunction, ShowModalFunction, ShowPromptFunction, ShowSnackbarFunction } from '@/components/modals/type'
import { SetCopyableModelsFunction, TriggerSaveJigErrorFunction, UpdateCopyableByListFunction } from '@/components/share/type'
import { AppConst, JigConst, MueConst, SubscriptionConst, UtilsConst } from '@/constants'
import FiltersMixin from '@/mixin/Filters'
import JigLock from '@/mixin/JigLock'
import JigPermissions from '@/mixin/JigPermissions'
import JigSharelink from '@/mixin/JigSharelink'
import { getInstance } from '@/plugins/auth0'
import { Tenant, TenantUser } from '@/store/modules/app/types'
import {
  JigCategoryOption,
  JigMetadata,
  JigShareURLData,
  JigVisibilityEventData,
  JigVisibilityListOption,
  JigVisibilityOption,
  Permission,
} from '@/store/modules/jig/types'
import { Namespace } from '@/store/types'
import { AppHelper } from '@/utils/app-helper'
import { eventBus } from '@/utils/eventBus'
import { ValidationRules } from '@/utils/input-validation'
import { JigHelpers } from '@/utils/jig-helpers'
import { TenantHelpers } from '@/utils/tenant-helpers'
import { segmentEventTracking } from '@/utils/tracking'
import IconDesktop from '@svg/IconDesktop.svg'
import IconCopy from '@svg/IconCopy.svg'
import IconInfoOutlined from '@svg/IconInfoOutlined.svg'
import IconShare from '@svg/Icon_Share.svg'
import IconView from '@svg/Icon_View.svg'
import IconGlobe from '@svg/IconGlobe.svg'
import IconLock from '@svg/IconLock.svg'
import IconShield from '@svg/IconShield.svg'
import IconTagProGold from '@svg/IconTagProGold.svg'
import axios from 'axios'
import { saveAs } from 'file-saver'
import { mixins } from 'vue-class-component'
import { Component, Vue, Watch } from 'vue-property-decorator'
import { Action, Getter, State } from 'vuex-class'

declare var consoleError: any

@Component({
  components: {
    'hint-tooltip': HintTooltip,
    'jig-action-modals': JigActionModals,
    'jig-text-field': JigTextField,
    'jig-tooltip': JigTooltip,
    'label-with-hint': LabelWithHint,
    MoreJigActions,
    'users-preview-list': UsersPreviewList,
    'team-rights': TeamRights,
    'export-jig-modal': ExportJigModal,
    'upload-thumbnail-modal': UploadThumbnail,
    'jig-warning-modals': JigWarningModals,
    'icon-desktop': IconDesktop,
    'icon-globe': IconGlobe,
    'icon-lock': IconLock,
    'icon-shield': IconShield,
    'icon-tag-pro-gold': IconTagProGold,
    IconCopy,
    IconInfoOutlined,
    IconShare,
    IconView,
  },
})
export default class JigDetailVue extends mixins(FiltersMixin, JigLock, JigPermissions, JigSharelink) {
  @State('jigDetailPermissions', { namespace: Namespace.Jig })
  public jigDetailPermissions!: Permission[]
  @State('jigCategoryOptions', { namespace: Namespace.Jig })
  public jigCategoryOptions!: JigCategoryOption[]
  @State('initializing', { namespace: Namespace.Utils })
  public initializing!: boolean

  @Getter('isFreeTier', { namespace: Namespace.App })
  public isFreeTier!: boolean
  @Getter('isOrgTier', { namespace: Namespace.App })
  public isOrgTier!: boolean
  @Getter('isPreparingApp', { namespace: Namespace.Utils })
  public isPreparingApp!: boolean

  @Action('executeStatisticReport', { namespace: Namespace.Jig }) public executeStatisticReport: any
  @Action('loadJigMetadata', { namespace: Namespace.Jig }) public loadJigMetadata: any
  @Action('loadJigPermissions', { namespace: Namespace.Jig }) public loadJigPermissions: any
  @Action('clearJigShareLink', { namespace: Namespace.Jig }) public clearJigShareLink: any
  @Action('updateJigMetadata', { namespace: Namespace.Jig }) public updateJigMetadata: any
  @Action('updateJigVisibility', { namespace: Namespace.Jig }) public updateJigVisibility: any

  public $refs!: Vue['$refs'] & {
    exportJigModal: ExportJigModal
    jigActionModals: JigActionModals
    jigTagsList: typeof JigTextField
    jigNameTip: HintTooltip
    teamRights: TeamRights
    uploadThumbnailModal: UploadThumbnail
    jigWarningModals: JigWarningModals
  }

  private currentTab = 0
  private isExporting: boolean = false

  private nameRules: any = ValidationRules.RequiredName

  // Published Jig is a Jig with visibility value `public`. To distinguish from `public sharing` Jig, we use `publish` in naming instead.
  private static publishVisibility: JigVisibilityOption = {
    icon: 'icon-desktop',
    text: JigConst.jigVisibilitiesStaff[JigConst.JigVisibilities.Public],
  }
  // Default visibility dropdown for all non-SuperUser/non-JigStaff users should contain just `link`, `link-password` and `private`.
  private visibilityItems: {
    [K in JigConst.JigVisibilities]?: JigVisibilityOption
  } = {
    [JigConst.JigVisibilities.Link]: {
      icon: 'icon-globe',
      text: JigConst.jigVisibilities[JigConst.JigVisibilities.Link],
    },
    [JigConst.JigVisibilities.Private]: {
      icon: 'icon-shield',
      text: JigConst.jigVisibilities[JigConst.JigVisibilities.Private],
    },
    [JigConst.JigVisibilities.Password]: {
      icon: 'icon-lock',
      text: JigConst.jigVisibilities[JigConst.JigVisibilities.Password],
    },
  }
  private categoryItems: any = []
  private isFormValid: boolean = false
  private promptPending: { [key: string]: boolean } = {
    // Flag var for when Jig visibility is changed to `public`
    publishingJig: false,
    // Flag var for when user toggles `Public sharing` switch
    isPublicSharing: false,
    // Flag var for when Jig visibility is changed from `link` to `private`
    linkJigToPrivate: false,
    // Flag var for when Jig visibility is changed from `link` to `link-password`
    linkJigToPassword: false,
    // If Jig password regenerate confirm is required
    jigPasswordRegenerate: false,
  }
  private usersWithAccess: TenantUser[] = []
  private previewLength: number = 4
  private visibilityOnLoad: string = ''
  private permissionsDirty: boolean = false
  private publicSharing: boolean = false
  private isCopyableByOthers: boolean = false
  private copyableBy: string = ''
  private pullApartAllowed: boolean = false
  private isLoadingJigDetails: boolean = true
  private jigCategoryRules: any = {
    select: [this.validateCategories.bind(this)],
  }
  private jigTags: string = ''
  private jigCategories: string = ''
  private jigVisibility: JigVisibilityListOption | null = null
  private hasAutoOpenedManageAccess: boolean = false

  // get jig view data after the jig details have been loaded.
  @Watch('jigMetadata')
  private async onJigDetailChanged() {
    if (typeof this.jigMetadata === 'undefined') {
      return
    }

    this.pullApartAllowed =
      this.jigMetadata.Features != null && this.jigMetadata.Features.options[SubscriptionConst.Features.AVPFeaturePullApartKey].enabled

    this.updateJigDataModels()
  }

  @Watch('myTenant')
  private onMyTenantChanged(value: Tenant) {
    this.myTenantID = value.ID
    this.updateMyTenantIndex()
    // Skip during app init: TeamRights isn't mounted yet (template is gated on
    // isPreparingApp). initializeData() runs once initializing flips to false
    // and refreshes TeamRights via props.
    if (this.initializing) {
      return
    }
    this.$callRefMethod<UpdateCopyableByListFunction>('teamRights', 'UpdateCopyableByList')
  }

  @Watch('initializing')
  private onInitializingChanged(value: boolean, oldValue: boolean) {
    if (!value) {
      this.initializeData()
    }
  }

  // Sometimes weirdly onChange event of v-switch won't be triggered successfully when v-model value is manually updated.
  // Change to watch event so it's alwasy triggered.
  @Watch('publicSharing')
  private onPublicSharingChanged(value: boolean) {
    this.promptPending.isPublicSharing = true

    // Determine the default target visibility based on publicSharing state
    const targetVisibility = this.publicSharing ? JigConst.JigVisibilities.Link : JigConst.JigVisibilities.Private

    // Check if current visibility needs to be updated
    // Need update when:
    // - If current visibility is null, or
    // - current visibility is private/password and publicSharing is true, or
    // - current visibility is link/public and publicSharing is false
    const needsUpdate =
      this.jigVisibility == null ||
      (this.publicSharing &&
        (this.jigVisibility.value === JigConst.JigVisibilities.Private ||
          this.jigVisibility.value === JigConst.JigVisibilities.Password)) ||
      (!this.publicSharing &&
        (this.jigVisibility.value === JigConst.JigVisibilities.Link || this.jigVisibility.value === JigConst.JigVisibilities.Public))

    if (needsUpdate) {
      this.jigVisibility = this.jigVisibilityOptions.find((option) => option.value === targetVisibility) || null
    }
  }

  private get ownerName(): string {
    const owner = this.tenantUsers.find((u: TenantUser) => u.Uid === this.jigMetadata.Uid)

    return owner != null ? owner.name || owner.username : ''
  }

  // Only count those with edit permission (inc. owner, editor, team manager/admin)
  private get individualPermDetails(): string {
    let editor: number = 0

    this.jigDetailPermissions.forEach((perm: Permission) => {
      if (perm.CRUD && perm.CRUD.U && perm.AudID !== this.jigMetadata.Uid) {
        // Can edit and not Jig owner
        editor += 1
      }
    })

    return `${editor} editor${editor === 1 ? '' : 's'}`
  }

  private get tenantUsersPreview(): TenantUser[] {
    const startIndex = this.usersWithAccess.length < this.previewLength ? this.usersWithAccess.length : this.previewLength
    const selectedUserUIDs = this.usersWithAccess.map((u: TenantUser) => u.Uid)

    return this.usersWithAccess
      .slice(0, startIndex)
      .concat(this.tenantUsers.filter((u: TenantUser) => !selectedUserUIDs.includes(u.Uid)).slice(0, this.previewLength - startIndex))
  }

  private get thumbnailAstcUrl(): string {
    return this.thumbnailURLMapCustomType(this.jigMetadata?.ThumbnailURL ?? '', 'astc')
  }

  private get thumbnailPngUrl(): string {
    return this.thumbnailURLMapCustomType(this.jigMetadata?.ThumbnailURL ?? '', 'png')
  }

  private openExportModal() {
    this.$refs.exportJigModal.show()
  }

  // Shared blob downloader for export actions. Pass isAbsolute=true when the URL is already fully
  // qualified (e.g. thumbnail URLs returned by thumbnailURLMapCustomType) to prevent double-prefixing.
  private async downloadBlob(url: string, filename: string, isAbsolute: boolean = false): Promise<void> {
    const authService = getInstance()
    const response = await axios.get(url, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${authService.accessToken}`,
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: this.myTenantID,
      },
      timeout: 120000, // 2 min as milliseconds
      validateStatus: (status) => status < 400,
      baseURL: isAbsolute ? '' : `${AppConst.apiDomain}`,
    })
    saveAs(response.data, filename)
    this.$refs.exportJigModal.close()
  }

  private handleExportError(logPrefix: string, userMessage: string, error: unknown): void {
    consoleError(`${logPrefix}: `, error)
    let message = userMessage
    if (axios.isAxiosError(error)) {
      if (error.response) {
        message += `Error ${error.response.status}: ${error.response.statusText}`
      } else if (error.request) {
        message += 'No response received from server.'
      } else {
        message += error.message
      }
    } else if (error instanceof Error) {
      message += error.message
    } else if (typeof error === 'string') {
      message += error
    }
    window.alert(message)
  }

  private async exportJigZip(): Promise<void> {
    if (typeof this.jigMetadata === 'undefined') {
      return
    }
    this.isExporting = true
    try {
      await this.downloadBlob(`${AppConst.APIEndpoint.jigsV2Url}/${this.jigMetadata.Id}?action=export`, `jig_${this.jigMetadata.Id}.zip`)
    } catch (error) {
      this.handleExportError('Jig zip download error', 'An error occurred while downloading the Jig zip file. ', error)
    } finally {
      this.isExporting = false
    }
  }

  private async exportJigThumbnail(): Promise<void> {
    if (!this.jigMetadata?.ThumbnailURL) {
      return
    }
    this.isExporting = true
    try {
      await this.downloadBlob(this.thumbnailPngUrl, `jig_${this.jigMetadata.Id}_thumbnail.png`, true)
    } catch (error) {
      this.handleExportError('Jig thumbnail download error', 'An error occurred while downloading the thumbnail. ', error)
    } finally {
      this.isExporting = false
    }
  }

  private get showJigActions(): boolean {
    return this.isOwner() || this.canEditOrDeleteJig() || this.teamCanReshare
  }

  private get shouldAutoOpenManageAccess(): boolean {
    return this.$route.query.openActions === 'true'
  }

  private get isPullApartConfigAllowed(): boolean {
    return this.jigMetadata.FormatVersionAtCreation > 8.8
  }

  private get isSuperUserOrJigStaff(): boolean {
    return TenantHelpers.IsSuperUserOrJigStaff()
  }

  private get publicSharingSwitchDisabled(): boolean {
    return !this.canEditOrDeleteJig() || this.isFreeTier
  }

  private get isPasswordVisibility(): boolean {
    return this.jigVisibility?.value === JigConst.JigVisibilities.Password
  }

  private get jigDisplayName(): string {
    if (this.isPreparingApp || this.isProcessing || this.jigMetadata == null) {
      return ''
    }

    if (!this.canEditOrDeleteJig() && !this.isLoadingJigDetails) {
      return this.jigMetadata.ProjectName + ' (view only)'
    }
    return this.jigMetadata.ProjectName
  }

  private get jigVisibilityOptions(): JigVisibilityListOption[] {
    return Object.keys(this.visibilityItems)
      .map((key) => {
        const typedKey = key as JigConst.JigVisibilities // Cast to JigConst.JigVisibilities
        const item = this.visibilityItems[typedKey]

        if (item != null) {
          return {
            ...item,
            value: typedKey,
            disabled:
              (this.isFreeTier && typedKey !== JigConst.JigVisibilities.Link) ||
              (!this.isOrgTier && typedKey === JigConst.JigVisibilities.Password),
          }
        } else {
          return null
        }
      })
      .filter((option): option is JigVisibilityListOption => option != null)
  }

  private get warningModalsConditions(): { [key: string]: boolean } {
    const conditions = { ...this.promptPending }
    delete conditions.isPublicSharing

    return conditions
  }

  protected created() {
    this.jigVisibility = this.jigVisibilityOptions[0]
  }

  private get previewUrl(): string {
    const jigId = this.jigMetadata?.Id || ''
    if (!jigId) {
      return ''
    }
    const envQuery = extractEnvFromApiDomain(AppConst.apiDomain)
    return 'https://viewer.jig.space/?jig=' + encodeURIComponent(jigId)
      + (envQuery ? '&env=' + encodeURIComponent(envQuery) : '')
  }

  protected beforeDestroy() {
    // Null the src so the browser tears down the WebGL context immediately
    // rather than waiting for Vue's destroy cycle to complete.
    const iframe = this.$el?.querySelector<HTMLIFrameElement>('.jig-preview__iframe')
    if (iframe) {
      iframe.src = ''
    }
  }

  protected mounted() {
    if (!this.initializing) {
      this.initializeData()
    }

    this.$on(this.jigEvents.onUpdatingJigVisibility, (payload: JigVisibilityEventData) => {
      if (payload.teamSharePermissions != null) {
        this.originalJigTeamSharePermissions = this.jigMetadata.Tenants ? [...this.jigMetadata.Tenants] : undefined
        this.isTeamShareConfigChange = payload.teamSharePermissions != null

        this.craftJigTeamSharingData(payload)
      }
    })
  }

  private async initializeData() {
    if (this.isSuperUserOrJigStaff) {
      Vue.set(this.visibilityItems, JigConst.JigVisibilities.Public, JigDetailVue.publishVisibility)
    } else {
      Vue.delete(this.visibilityItems, JigConst.JigVisibilities.Public)
    }

    await this.loadJigAndReset()
    this.$callRefMethod<UpdateCopyableByListFunction>('teamRights', 'UpdateCopyableByList')
    this.$callRefMethod<SetCopyableModelsFunction>('teamRights', 'SetCopyableModels', {
      isCopyableByOthers: this.isCopyableByOthers,
      copyableBy: this.copyableBy,
    })
    this.tryAutoOpenManageAccessFromQuery()
  }

  private updateJigDataModels() {
    this.jigTags = this.jigMetadata.Tags.join(', ')
    this.jigVisibility = this.jigVisibilityOptions.find(
      (v: JigVisibilityListOption) => v.value === this.jigMetadata.ProjectVisibility
    ) as JigVisibilityListOption

    if (!this.isSuperUserOrJigStaff) {
      this.jigCategories = this.jigMetadata.Categories.join(', ')
    }
  }

  private validateCategories(v: string[]) {
    const standardCat = v.filter((cat: string) => this.jigCategoryOptions.find((op: any) => op.value === cat) != null)

    return (standardCat && standardCat.length <= 1) || `Please update to only one category (or none)`
  }

  private async loadJigAndReset() {
    this.isLoadingJigDetails = true

    await this.loadJigMetadata({
      jigId: this.$route.params.id,
    })
    await this.loadJigPermissions({
      jigId: this.$route.params.id,
    })

    this.usersWithAccess = this.tenantUsers.filter(
      (user: TenantUser) =>
        this.jigDetailPermissions.find((perm: Permission) => perm.AudID === user.Uid && perm.CRUD && (perm.CRUD.U || perm.CRUD.R)) != null
    )

    this.publicSharing =
      this.jigMetadata.ProjectVisibility !== JigConst.JigVisibilities.Private &&
      this.jigMetadata.ProjectVisibility !== JigConst.JigVisibilities.Password
    this.visibilityOnLoad = this.jigMetadata.ProjectVisibility
    this.addNonStandardCategoriesWithWarning(this.jigMetadata.Categories)
    this.myTenantID = this.myTenant.ID
    this.updateMyTenantIndex()
    this.pullApartAllowed =
      this.jigMetadata.Features != null && this.jigMetadata.Features.options[SubscriptionConst.Features.AVPFeaturePullApartKey].enabled

    if (this.jigMetadata.Tenants) {
      // When the permissions are removed completely, Permissions might return null so we need to assign empty array as fallback
      this.updateTeamSharePermissions(
        this.jigMetadata.Tenants[this.myTenantIndex] && this.jigMetadata.Tenants[this.myTenantIndex].Permissions
          ? this.jigMetadata.Tenants[this.myTenantIndex].Permissions
          : []
      )

      this.teamSharePermModel = this.teamSharePermissions
      this.teamSharePermModelOldVal = this.teamSharePermissions
    }

    if (this.jigMetadata.CopyableBy === JigConst.JigCopyable.Owner) {
      this.isCopyableByOthers = false
      this.copyableBy = JigConst.JigCopyable.Anyone // default
    } else if (this.jigMetadata.CopyableBy) {
      this.isCopyableByOthers = true
      this.copyableBy = this.jigMetadata.CopyableBy
    }

    this.updateJigDataModels()

    this.isLoadingJigDetails = false
  }

  private addNonStandardCategoriesWithWarning(categories: string[]): any {
    this.categoryItems = this.jigCategoryOptions
    const nonStandardCategories = categories.filter((x: string) => !this.jigCategoryOptions.some((y: JigCategoryOption) => y.value === x))
    this.categoryItems = this.categoryItems.concat(
      nonStandardCategories.map((z: string) => ({
        text: `${z} (not standard)`,
        value: z,
      }))
    )
  }

  private showDeleteJigPrompt() {
    eventBus.$emit(this.rootJigEvents.onJigDelete, {
      jigId: this.jigMetadata.Id,
      jigName: this.jigMetadata.ProjectName,
      jigFormatVersion: this.jigMetadata.FormatVersion,
    })
  }

  private async showCopyJigPrompt() {
    eventBus.$emit(this.rootJigEvents.showCopyJigPrompt, {
      jigId: this.jigMetadata.Id,
      jigName: this.jigMetadata.ProjectName,
      visibility: this.jigMetadata.ProjectVisibility,
    })
  }

  private onTeamShareModelsChanged(value: any) {
    // Check relevant logic and update accordingly, for example reshare can only be enabled when view is enabled.
    this.onTeamShareChanged(value)
  }

  private onJigVisibilityChange(option: any) {
    this.publicSharing = option.value !== JigConst.JigVisibilities.Private && option.value !== JigConst.JigVisibilities.Password
    this.promptPending.publishingJig = false
    this.promptPending.linkJigToPrivate = false
    this.promptPending.linkJigToPassword = false
    this.promptPending.jigPasswordRegenerate = false

    if (this.visibilityOnLoad !== JigConst.JigVisibilities.Public) {
      if (option.value === JigConst.JigVisibilities.Public) {
        // Visibility value `public` is selected from visibility dropdown
        this.promptPending.publishingJig = true
      } else {
        // Visibility value `link` / `link-password` / `private` is selected
        this.promptPending.publishingJig = false

        if (this.visibilityOnLoad === JigConst.JigVisibilities.Link && option.value === JigConst.JigVisibilities.Private) {
          // Jig visibility on page load is `link` and `private` is selected
          this.promptPending.linkJigToPrivate = true
        } else if (this.visibilityOnLoad === JigConst.JigVisibilities.Link && option.value === JigConst.JigVisibilities.Password) {
          // Jig visibility on page load is `link` and `link-password` is selected
          this.promptPending.linkJigToPassword = true
        } else if (
          this.visibilityOnLoad === JigConst.JigVisibilities.Private &&
          option.value === JigConst.JigVisibilities.Password &&
          this.jigMetadata.Password
        ) {
          // Jig visibility on page load is `private` and `link-password` is selected
          this.promptPending.jigPasswordRegenerate = true
        }
      }
    } else if (option.value === JigConst.JigVisibilities.Private) {
      // Jig visibility on page load is `public` and `private` is selected
      this.promptPending.linkJigToPrivate = true
    } else if (option.value === JigConst.JigVisibilities.Password) {
      // Jig visibility on page load is `public` and `link-password` is selected
      this.promptPending.jigPasswordRegenerate = true
    }
  }

  private onCopyableByChange(copyableBy: string) {
    this.copyableBy = copyableBy
  }

  private onCopyableByOthersChange(isCopyableByOthers: boolean) {
    this.isCopyableByOthers = isCopyableByOthers
  }

  private checkingPublicSharingPublish(): boolean {
    if (this.jigVisibility == null) {
      return false
    }

    if (
      (this.visibilityOnLoad === JigConst.JigVisibilities.Private || this.visibilityOnLoad === JigConst.JigVisibilities.Password) &&
      this.publicSharing &&
      this.jigVisibility.value !== JigConst.JigVisibilities.Public
    ) {
      // Jig visibility is `private` or `link-passwrod` on page load and we now turned on `Public sharing` switch.
      // Exclude the situation when JigStaff/SuperUser has set visibility value to `public`
      this.$callRefMethod<ShowPromptFunction>('jigWarningModals', 'ShowPrompt', 'privateJigToLinkPromptYesNo')
      return true
    } else if (
      this.visibilityOnLoad === JigConst.JigVisibilities.Link &&
      !this.publicSharing &&
      this.jigVisibility.value !== JigConst.JigVisibilities.Password
    ) {
      // Jig visibility is `link` on page load and we selected `private` visibility, or we now turned off `Public sharing` switch.
      this.$callRefMethod<ShowPromptFunction>('jigWarningModals', 'ShowPrompt', 'publicJigToPrivatePromptYesNo')
      return true
    }
    // All other situations, no warning prompt is required. For example `link-password` to `private`.
    return false
  }

  private async onWarningPrompConfirmed(modalRefs: string[], customFunc: () => void, regeneratePassword?: boolean) {
    this.isProcessingPromptYesNo = true
    modalRefs.forEach((modalRef: string) => this.$callRefMethod<ClosePromptFunction>('jigWarningModals', 'ClosePrompt', modalRef))

    customFunc()

    await this.saveChanges(regeneratePassword)
    this.isProcessingPromptYesNo = false
  }

  private async publishCheckedThenSaveChanges() {
    await this.onWarningPrompConfirmed(['publishingJigPromptYesNo'], () => {
      this.promptPending.publishingJig = false
    })
  }

  private async changeJigToPrivateConfirmed() {
    await this.onWarningPrompConfirmed(['publicJigToPrivatePromptYesNo', 'linkJigToPrivatePromptYesNo'], () => {
      this.publicSharing = false
      this.jigVisibility = this.jigVisibilityOptions.find(
        (option: JigVisibilityListOption) => option.value === JigConst.JigVisibilities.Private
      ) as JigVisibilityListOption
      this.promptPending.linkJigToPrivate = false
    })
  }

  private async regeneratePasswordConfirmed(regeneratePassword: boolean) {
    await this.onWarningPrompConfirmed(
      ['jigPasswordRegeneratePromptYesNo', 'linkJigToPasswordPromptYesNo'],
      () => {
        this.promptPending.linkJigToPassword = false
        this.jigVisibility = this.jigVisibilityOptions.find(
          (option: JigVisibilityListOption) => option.value === JigConst.JigVisibilities.Password
        ) as JigVisibilityListOption
        this.promptPending.jigPasswordRegenerate = false
      },
      regeneratePassword
    )
  }

  private async requestChangeJigToLink() {
    await this.onWarningPrompConfirmed(['privateJigToLinkPromptYesNo'], () => {
      this.publicSharing = true
      this.jigVisibility = this.jigVisibilityOptions.find(
        (option: JigVisibilityListOption) => option.value === JigConst.JigVisibilities.Link
      ) as JigVisibilityListOption
    })
  }

  private async saveChanges(regeneratePassword: boolean | null | undefined = null) {
    this.isProcessing = true

    const newJigMetadata = AppHelper.deepCopyNestedJsonSafeObject(this.jigMetadata)
    const queryParams: any = {}
    let telemetryRequired: boolean = false

    // Get the key of any visibility change relevant warning prompt that is required to be shown
    const warningModalRef = JigHelpers.checkForWarningPrompt(this.warningModalsConditions)
    if (warningModalRef != null) {
      this.$callRefMethod<ShowPromptFunction>('jigWarningModals', 'ShowPrompt', `${warningModalRef}PromptYesNo`)
      return
    }

    if (!this.publicSharingSwitchDisabled && this.promptPending.isPublicSharing) {
      this.promptPending.isPublicSharing = false
      if (this.checkingPublicSharingPublish()) {
        return
      }
    }

    const isLockObtained = await this.RequestNewLock()
    if (!isLockObtained) {
      return
    }

    JigHelpers.sanitiseJigData(newJigMetadata, this.jigTags, this.isSuperUserOrJigStaff ? null : this.jigCategories)

    if (this.jigVisibility != null) {
      newJigMetadata.ProjectVisibility =
        this.jigVisibility.value === JigConst.JigVisibilities.Link && !this.publicSharing
          ? JigConst.JigVisibilities.Private
          : this.jigVisibility.value
    }

    if (this.isOwner() || this.canEditOrDeleteJig()) {
      if (!newJigMetadata.Tenants) {
        newJigMetadata.Tenants = []
      }

      if (this.isCopyableByOthers) {
        newJigMetadata.CopyableBy = this.copyableBy
      } else {
        newJigMetadata.CopyableBy = JigConst.JigCopyable.Owner
      }
    }

    if (this.visibilityOnLoad !== newJigMetadata.ProjectVisibility) {
      // If visibility changes we need to clear our local cache of the branch link because
      // it may change how the link is generated.
      this.clearJigShareLink({
        jigId: newJigMetadata.Id,
        requestInFlight: false,
        shareURL: '',
      } as JigShareURLData)

      if (newJigMetadata.ProjectVisibility === JigConst.JigVisibilities.Password) {
        queryParams.regenPassword = regeneratePassword
      } else {
        delete queryParams.regenPassword
      }
    }

    if (
      (newJigMetadata.Features == null && this.pullApartAllowed) ||
      (newJigMetadata.Features &&
        this.pullApartAllowed !== newJigMetadata.Features.options[SubscriptionConst.Features.AVPFeaturePullApartKey].enabled)
    ) {
      telemetryRequired = true

      newJigMetadata.Features.options[SubscriptionConst.Features.AVPFeaturePullApartKey].enabled = this.pullApartAllowed
    }

    const metaUpdateResponse = await this.updateJigMetadata({
      jigMetadata: newJigMetadata,
      queryParams,
    })

    await this.releaseLock(this.lockPayload)

    if (metaUpdateResponse.isSuccess) {
      if (telemetryRequired) {
        segmentEventTracking('PullApartSet', {
          ...this.eventTracking(),
          origin: 'Dashboard',
          jigId: newJigMetadata.Id,
          jigName: newJigMetadata.ProjectName,
          tenantName: this.myTenant.Name,
          tenantId: this.myTenantID,
          state: this.pullApartAllowed,
        })
      }
      if (this.isTeamShareConfigChange) {
        this.teamShareEventTrack(this.currentPermission, this.newTeamPermissions)
      }
      this.resetTeamSharePermissions()
      await this.loadJigAndReset()
    } else {
      if (metaUpdateResponse.status === MueConst.nonSystemErrors.unprocessableEntity.status) {
        this.$callRefMethod<TriggerSaveJigErrorFunction>(
          'jigWarningModals',
          'TriggerSaveJigError',
          MueConst.nonSystemErrors.unprocessableEntity.copy
        )
      }

      if (this.isTeamShareConfigChange) {
        // reset team share
        this.resetTeamSharePermissions()
      }
    }

    this.isTeamShareConfigChange = false
    this.resetSavingStatus()
  }

  private resetSavingStatus() {
    this.isProcessing = false
  }

  private async transferJigOwnership(payload: JigMetadata) {
    eventBus.$emit(this.rootJigEvents.onJigTransfer, payload)
  }

  private openImageUploadModal() {
    this.$callRefMethod<ShowModalFunction>('uploadThumbnailModal', 'ShowModal')
  }

  private async onLoadResetRequested(doneCallback: () => void) {
    await this.loadJigAndReset()
    doneCallback() // signal back to thumbnail upload component that it's done
  }

  private async onDataReloadRequested(doneCallback: () => void) {
    await this.reloadData()
    doneCallback() // signal back to thumbnail upload component that it's done
  }

  private onUpdateJigShareConfig(payload: any) {
    this.permissionsDirty = payload.permissionsDirty
  }

  private async showJigPermissionsPrompt() {
    this.$refs.jigActionModals.ShowJigPermissionsPromptOnly([this.jigMetadata])
  }

  private tryAutoOpenManageAccessFromQuery() {
    if (!this.shouldAutoOpenManageAccess || this.hasAutoOpenedManageAccess || !this.jigMetadata || !this.jigMetadata.Id) {
      return
    }

    this.hasAutoOpenedManageAccess = true
    this.$nextTick(() => {
      this.showJigPermissionsPrompt()
    })

    const queryParams: any = { ...this.$route.query }
    delete queryParams.openActions
    this.$router.replace({
      query: queryParams,
    })
  }

  private onSharePromptClose(payload: any) {
    // If any request actually happened in sharePrompt, reset jig data regardless success or failure, so we can get the latest Jig data.
    if (payload.updateResults != null) {
      this.permissionsDirty = true
    }

    this.loadJigIfPermissionsDirty()
  }

  private async reloadData() {
    this.permissionsDirty = true
    await this.loadJigIfPermissionsDirty()
  }

  private async loadJigIfPermissionsDirty() {
    if (this.permissionsDirty) {
      this.permissionsDirty = false
      await this.loadJigAndReset()
    }
  }

  // Routes through ShareModal via eventBus so the mixin's activity tracking and
  // deeplink resolution logic is reused. copyJigPassword uses $callRefMethod directly
  // because it owns the value and needs no mixin involvement.
  private onCopyShareableLink(payload: any) {
    eventBus.$emit(this.rootJigEvents.onCopyShareableLink, {
      visibility: this.jigMetadata.ProjectVisibility,
      data: {
        deeplink: this.jigMetadata.DeeplinkURL,
        showSuccessMessage: true,
        activity: 'JigSharedViaShareableLink',
        event: payload.event,
      },
    })
  }

  private async copyJigPassword() {
    const password = this.jigMetadata.Password || ''
    if (!password) return

    try {
      await window.navigator.clipboard.writeText(password)
      this.$callRefMethod<ShowSnackbarFunction>('jigActionModals', 'showClipboardSnackbar', 'Password copied to clipboard!', password)
    } catch (err) {
      consoleError('Failed to copy password to clipboard:', err)
      this.$callRefMethod<ShowSnackbarFunction>('jigActionModals', 'showErrorSnackbar', 'Failed to copy password')
    }
  }

  private goBack() {
    if ((window && window.history.length > 1) || (history && history.length > 1)) {
      this.$router.back()
    } else {
      this.$router.push('/my-jigs')
    }
  }
}
